import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { FormBuilder, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { Intercom, shutdown } from '@intercom/messenger-js-sdk';
import { AuthAppService } from 'avn/modules/services/auth-app/auth-app.service';
import { Constants } from 'avn/modules/services/constants';
import { SharedCompsService } from 'avn/modules/services/shared-comps.service';
import { StoreService } from 'avn/modules/services/store.service';
import { UserService } from 'avn/modules/services/user.service';
import { InputSafetyDirective } from 'avn/modules/shared/directives/input-safety.directive';
import { User } from 'avn/modules/shared/interfaces/user';

const ACCOUNT_LOCKED_STATUS = 500;
const SNACKBAR_SUCCESS_DURATION_SECONDS = 5;

@Component({
  selector: 'app-sign-in',
  templateUrl: './sign-in.html',
  styleUrls: ['./sign-in.scss'],
  imports: [FormsModule, ReactiveFormsModule, InputSafetyDirective]
})
export class SignInComponent implements OnInit, OnDestroy {
  public loading = false;
  public readonly hidePassword = signal(true);
  public wellbeingData: any;
  public selectedTab = 'signin';
  public loginType = Constants.LOGIN_TYPE;
  public hmacHash = '';
  public redirectUrl!: string;
  public subscriptions: any[] = [];

  public constructor(
    private readonly fb: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly api: UserService,
    private readonly authService: AuthAppService,
    private readonly snackBar: SharedCompsService,
    private readonly auth: AuthService,
    private readonly store: StoreService
  ) {}

  public signInForm = this.fb.group({
    email: ['', [Validators.required]],
    password: ['', [Validators.required]]
  });

  public ngOnInit(): void {
    if (this.loginType === 'normal') {
      const sub1 = this.route.queryParams.subscribe((params: any) => {
        this.redirectUrl = params['redirect'];
      });

      this.subscriptions.push(sub1);
    } else {
      this.loginAuth0();
    }
  }

  public loginAuth0(): void {
    this.auth.idTokenClaims$.subscribe((id: any) => {
      if (id?.__raw) {
        shutdown();
        this.authService.setAuth(id.__raw);
        this.authService.setworkcoverLoggedUser(id);
        this.loginAuth0Customer();
        this.hmacHash = this.store.generateHmac(id?.email, Constants.INTERCOM_CONFIG.secretKey);
        this.inItIntercom(id);
      } else {
        this.auth.isAuthenticated$.subscribe((au: any) => {
          if (!au) {
            this.auth.loginWithRedirect();
          }
        });
      }
    });
  }

  public inItIntercom(user: any): void {
    if (user) {
      Intercom({
        app_id: Constants.INTERCOM_CONFIG.appId,
        user_name: user?.name,
        email: user?.email,
        platform: user?.platform,
        user_hash: this.hmacHash
      });
    }
  }

  public loginAuth0Customer(): void {
    this.authService.clientIdType = 'frontend';
    const session_path = localStorage.getItem('current_path');
    this.api.getConfigData().subscribe((response: any) => {
      Constants.setRuntimeConfig(response);
    });
    if (session_path) {
      this.router.navigate([session_path]);
    } else {
      this.router.navigate(['/'], { relativeTo: this.route });
    }
  }

  public showPasswordToggle(): void {
    this.hidePassword.update((hide) => !hide);
  }

  public signInUser(): void {
    this.loading = true;
    const loginObj = { ...this.signInForm.value };
    this.loginCall({ email: loginObj.email, password: loginObj.password });
  }

  public loginCall(obj: any): void {
    const responseObs = this.api.login(obj).subscribe({
      next: (response) => {
        const decryptText = response;
        try {
          const resp = typeof decryptText === 'string' ? JSON.parse(decryptText) : decryptText;
          this.loginResponse(resp);
        } catch {
          this.snackBar.showSnackBar('Error in Authentication. Please try again after sometime');
          this.authService.purgeAuth();
        }
      },
      error: (error) => {
        this.loading = false;
        let errormsg = '';
        if (error?.error?.message) {
          errormsg = error?.error?.message;
        } else {
          errormsg = 'Error in Authentication. Please try again after sometime';
        }
        this.snackBar.showSnackBar(errormsg);
        this.authService.purgeAuth();
      }
    });
    this.subscriptions.push(responseObs);
  }

  public loginResponse(response: any): void {
    if (response.status === ACCOUNT_LOCKED_STATUS) {
      this.loading = false;
      let errormsg = '';
      const idx = response.Message.indexOf('Your Account is Locked. Reset your Login using Forgot Password? link');
      if (idx !== -1) {
        errormsg = 'Your Account is locked. You can unlock by resetting your password';
      } else {
        errormsg = 'Incorrect email or password';
      }
      this.snackBar.showSnackBar(errormsg);
      this.authService.purgeAuth();
      return;
    }
    this.snackBar.showSnackBar('Login Successful', SNACKBAR_SUCCESS_DURATION_SECONDS);
    this.successHandler(response);
    this.navigateToPage(response);
  }

  public successHandler(response: any): void {
    this.authService.setAuth(response?.auth_token);
    const user = <User>response?.user;
    user.businesses = response.header_detail.businesses;
    user.header_detail = response.header_detail;
    this.authService.setworkcoverLoggedUser(user);
    if (response.header_detail) {
      this.wellbeingData = response.header_detail.wellbeing;
      this.authService.setWellbeingDetails(response.header_detail.wellbeing);
    }
    this.loading = false;
    this.snackBar.showSnackBar(`Welcome back <b>${user.first_name}</b>`, SNACKBAR_SUCCESS_DURATION_SECONDS);
    this.router.navigate(['/']);
  }

  public navigateToPage(response: any): void {
    if (
      (response.user.is_owner || response.user.is_group_owner || response.user.is_practitioner) &&
      response.header_detail?.businesses?.length > 0
    ) {
      // business user
      const businesses = response.header_detail.businesses;
      const businessId = businesses[0].uuid;
      this.router.navigate(['/business/' + String(businessId)]);
    } else {
      console.info(this.redirectUrl);
      if (this.redirectUrl) {
        this.router.navigate([this.redirectUrl]);
      } else {
        this.router.navigate(['/']);
      }
    }
    // if (response.user.is_wellbeing_owner) {
    //   // wellbeing user
    //   this.router.navigate(['/wellbeing']);
    // } else
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => {
      try {
        sub.unsubscribe();
      } catch {
        console.info('');
      }
    });
  }
}
